import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, DollarSign, Search, X,
  Paperclip, ArrowLeftRight, Download, FileText,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { TransactionForm } from '../components/TransactionForm';
import { CambioForm } from '../components/CambioForm';
import {
  Transaction,
  RegisterEntry,
  BancoMCPReconciliationAccount,
  getTransactions,
  getTransaction,
  getDeclaredAccounts,
  getAccounts,
  getRegisterMulti,
  getBancoMCPReconciliationStatus,
  downloadRegisterPDF,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  linkTransfer,
} from '../services/apiService';
import { uploadDocument, getTransactionDocuments, downloadDocument, DocumentEntry } from '../services/documentService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { AccountAutocomplete } from '../components/ui/AccountAutocomplete';
import { SaldoMCPBadge } from '../components/ui/SaldoMCPBadge';

const PAGE_SIZE = 50;

const fmtAmount = (value: number, commodity: string): string => {
  const abs = Math.abs(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 ? '-' : ''}${commodity} ${abs}`;
};

type FormMode = 'none' | 'create' | 'edit' | 'cambio';

export default function TransactionsPage() {
  const { canWrite, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL as source of truth for account selection and date/description filters
  const selectedAccounts = searchParams.getAll('account');
  const filterBegin = searchParams.get('begin') || '';
  const filterEnd = searchParams.get('end') || '';
  const filterDescription = searchParams.get('description') || '';

  const mode = selectedAccounts.length > 0 ? 'register' : 'list';

  // Stable key for useCallback deps (arrays aren't stable refs)
  const selectedAccountsKey = selectedAccounts.join('||');

  // Account autocomplete input (local — not URL-synced until an account is selected)
  const [accountInput, setAccountInput] = useState('');
  const [allAccounts, setAllAccounts] = useState<string[]>([]);

  // List mode state
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [declaredAccounts, setDeclaredAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(mode === 'list');
  const [formMode, setFormMode] = useState<FormMode>('none');
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [docPopover, setDocPopover] = useState<string | null>(null);
  const [docPopoverDocs, setDocPopoverDocs] = useState<DocumentEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState(false);
  const [filterAccount, setFilterAccount] = useState(''); // local, not URL-synced

  // Register mode state
  const [regEntries, setRegEntries] = useState<RegisterEntry[]>([]);
  const [regLoading, setRegLoading] = useState(mode === 'register');
  const [regLoadingMore, setRegLoadingMore] = useState(false);
  const [regHasMore, setRegHasMore] = useState(false);
  const [regTotal, setRegTotal] = useState(0);
  const [regError, setRegError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [regEditTxn, setRegEditTxn] = useState<Transaction | null>(null);
  const [regEditLoading, setRegEditLoading] = useState<string | null>(null);
  const [mcpRecord, setMcpRecord] = useState<BancoMCPReconciliationAccount | null>(null);

  // Load accounts on mount (for autocomplete and transaction forms)
  useEffect(() => {
    getAccounts().then(res => setAllAccounts(res.accounts)).catch(() => {});
    getDeclaredAccounts().then(res => setDeclaredAccounts(res.accounts)).catch(() => {});
  }, []);

  useEffect(() => {
    const accounts = selectedAccountsKey.split('||').filter(Boolean);
    if (accounts.length !== 1) {
      setMcpRecord(null);
      return;
    }
    let cancelled = false;
    getBancoMCPReconciliationStatus()
      .then(records => {
        if (cancelled) return;
        setMcpRecord(records.find(r => r.ledger_account === accounts[0]) ?? null);
      })
      .catch(() => { if (!cancelled) setMcpRecord(null); });
    return () => { cancelled = true; };
  }, [selectedAccountsKey]);

  // URL update helpers
  const addAccount = useCallback((account: string) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev.toString());
      params.append('account', account);
      return params;
    }, { replace: false });
  }, [setSearchParams]);

  const removeAccount = useCallback((account: string) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev.toString());
      const remaining = params.getAll('account').filter(a => a !== account);
      params.delete('account');
      for (const acc of remaining) params.append('account', acc);
      return params;
    }, { replace: false });
  }, [setSearchParams]);

  const updateFilter = useCallback((key: string, value: string) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev.toString());
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Multi-account selector: select when the typed value exactly matches an account
  const handleAccountInputChange = (value: string) => {
    if (allAccounts.includes(value) && !selectedAccounts.includes(value)) {
      addAccount(value);
      setAccountInput('');
    } else {
      setAccountInput(value);
    }
  };

  // Register mode: fetch one page of register entries
  const fetchRegisterPage = useCallback(async (offset: number, append: boolean) => {
    const accounts = selectedAccountsKey.split('||').filter(Boolean);
    if (accounts.length === 0) return;
    if (offset === 0) setRegLoading(true);
    else setRegLoadingMore(true);
    setRegError('');
    try {
      const res = await getRegisterMulti({
        accounts,
        begin: filterBegin || undefined,
        end: filterEnd || undefined,
        offset,
        limit: PAGE_SIZE,
      });
      setRegEntries(prev => append ? [...prev, ...res.entries] : res.entries);
      setRegHasMore(res.has_more);
      setRegTotal(res.total);
    } catch {
      setRegError('Erro ao carregar extrato');
    } finally {
      setRegLoading(false);
      setRegLoadingMore(false);
    }
  }, [selectedAccountsKey, filterBegin, filterEnd]);

  // Reset and reload register when accounts or date filters change
  useEffect(() => {
    if (mode !== 'register') return;
    setRegEntries([]);
    setRegHasMore(false);
    fetchRegisterPage(0, false);
  }, [mode, fetchRegisterPage]);

  // Infinite scroll observer for register mode
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || mode !== 'register') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && regHasMore && !regLoadingMore && !regLoading) {
          fetchRegisterPage(regEntries.length, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [mode, regHasMore, regLoadingMore, regLoading, regEntries.length, fetchRegisterPage]);

  // List mode: load transactions (debounced)
  const loadData = useCallback(async (signal?: AbortSignal) => {
    try {
      const params: Record<string, string> = {};
      if (filterBegin) params.begin = filterBegin;
      if (filterEnd) params.end = filterEnd;
      if (filterAccount) params.account = filterAccount;
      if (filterDescription) params.description = filterDescription;
      const xactRes = await getTransactions(params, signal);
      if (!signal?.aborted) {
        setTransactions(xactRes.transactions);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Failed to load transactions', err);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filterBegin, filterEnd, filterAccount, filterDescription]);

  useEffect(() => {
    if (mode !== 'list') return;
    setLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => loadData(controller.signal), 400);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [mode, loadData]);

  // PDF download (single-account only)
  const handleDownloadPDF = async () => {
    if (selectedAccounts.length !== 1 || pdfDownloading) return;
    setPdfDownloading(true);
    try {
      await downloadRegisterPDF(selectedAccounts[0], filterBegin || undefined, filterEnd || undefined);
    } catch {
      // ignore — browser already shows native error
    } finally {
      setPdfDownloading(false);
    }
  };

  // CRUD helpers
  const uploadFilesForTransaction = async (files: File[], date: string, payee: string) => {
    for (const file of files) {
      try {
        await uploadDocument(file, 'comprovante', '', date, payee);
      } catch (err) {
        console.warn('Failed to upload document:', file.name, err);
      }
    }
  };

  const handleCreate = async (data: any, pendingFiles?: File[]) => {
    await createTransaction(data);
    if (pendingFiles?.length) {
      await uploadFilesForTransaction(pendingFiles, data.date.replace(/\//g, '-'), data.payee);
    }
    setFormMode('none');
    setLoading(true);
    await loadData();
  };

  const handleUpdate = async (data: any, pendingFiles?: File[]) => {
    if (!editingTransaction) return;
    await updateTransaction(editingTransaction.id, data);
    if (pendingFiles?.length) {
      await uploadFilesForTransaction(pendingFiles, data.date.replace(/\//g, '-'), data.payee);
    }
    setFormMode('none');
    setEditingTransaction(null);
    setLoading(true);
    await loadData();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este lançamento?')) return;
    setDeleting(id);
    try {
      await deleteTransaction(id);
      setLoading(true);
      await loadData();
    } catch (err) {
      console.error('Failed to delete transaction', err);
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (xact: Transaction) => {
    setEditingTransaction(xact);
    setFormMode('edit');
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLinkTransfer = async () => {
    if (selected.size !== 2) return;
    const [a, b] = Array.from(selected);
    if (!window.confirm(
      'Vincular os dois lançamentos selecionados como UMA transferência? ' +
      'O lançamento duplicado é removido e os fingerprints do banco são ' +
      'preservados (a sincronização do banco-mcp continua funcionando).',
    )) return;
    setLinking(true);
    try {
      await linkTransfer({ txn_id_a: a, txn_id_b: b });
      setSelected(new Set());
      setLoading(true);
      await loadData();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Falha ao vincular como transferência.');
    } finally {
      setLinking(false);
    }
  };

  const handleDocClick = async (xactId: string) => {
    if (docPopover === xactId) { setDocPopover(null); return; }
    try {
      const res = await getTransactionDocuments(xactId);
      setDocPopoverDocs(res.documents);
      setDocPopover(xactId);
    } catch {
      console.error('Failed to load documents for transaction', xactId);
    }
  };

  const handleDocDownload = async (key: string) => {
    try {
      const blob = await downloadDocument(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key.split('/').pop() || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      console.error('Failed to download document');
    }
  };

  const handleRegisterEditClick = async (entry: RegisterEntry) => {
    if (!entry.txn_id) return;
    setRegEditLoading(entry.txn_id);
    try {
      const txn = await getTransaction(entry.txn_id);
      setRegEditTxn(txn);
    } catch {
      console.error('Failed to load transaction for editing');
    } finally {
      setRegEditLoading(null);
    }
  };

  const handleRegisterUpdate = async (data: any, pendingFiles?: File[]) => {
    if (!regEditTxn) return;
    await updateTransaction(regEditTxn.id, data);
    if (pendingFiles?.length) {
      await uploadFilesForTransaction(pendingFiles, data.date.replace(/\//g, '-'), data.payee);
    }
    setRegEditTxn(null);
    setRegEntries([]);
    await fetchRegisterPage(0, false);
  };

  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const initialLoading = loading && transactions.length === 0 && !filterAccount && !filterDescription;
  const availableAccounts = allAccounts.filter(a => !selectedAccounts.includes(a));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Lançamentos</h1>
          <SaldoMCPBadge record={mcpRecord} />
        </div>
        <div className="flex items-center gap-2">
          {selectedAccounts.length === 1 && (
            <button
              onClick={handleDownloadPDF}
              disabled={pdfDownloading}
              className="flex items-center gap-1.5 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 border border-gray-300"
              title="Baixar extrato em PDF"
            >
              <Download className="h-4 w-4" />
              PDF
            </button>
          )}
          {canWrite && (
            <>
              <button
                onClick={() => { setFormMode('cambio'); setEditingTransaction(null); }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
              >
                <DollarSign className="h-4 w-4" /> Câmbio
              </button>
              <button
                onClick={() => { setFormMode('create'); setEditingTransaction(null); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Novo Lançamento
              </button>
            </>
          )}
        </div>
      </div>

      {/* Transaction form — list mode only */}
      {mode === 'list' && formMode !== 'none' && (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {formMode === 'cambio'
                ? 'Novo Recebimento do Exterior'
                : formMode === 'edit'
                ? 'Editar Lançamento'
                : 'Novo Lançamento'}
            </h2>
            <button
              onClick={() => { setFormMode('none'); setEditingTransaction(null); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {formMode === 'cambio' ? (
            <CambioForm
              accounts={declaredAccounts}
              onSubmit={handleCreate}
              onCancel={() => setFormMode('none')}
            />
          ) : (
            <TransactionForm
              accounts={declaredAccounts}
              initial={formMode === 'edit' ? editingTransaction : null}
              onSubmit={formMode === 'edit' ? handleUpdate : handleCreate}
              onCancel={() => { setFormMode('none'); setEditingTransaction(null); }}
            />
          )}
        </div>
      )}

      {mode === 'register' && regEditTxn && (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Editar Lançamento</h2>
            <button
              onClick={() => setRegEditTxn(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <TransactionForm
            accounts={declaredAccounts}
            initial={regEditTxn}
            onSubmit={handleRegisterUpdate}
            onCancel={() => setRegEditTxn(null)}
          />
        </div>
      )}

      {/* Multi-account selector */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium text-gray-700">Extrato por conta</span>
          {selectedAccounts.length === 0 && (
            <span className="text-xs text-gray-400">
              selecionar 1 ou mais contas ativa o modo extrato com saldo
            </span>
          )}
        </div>
        {selectedAccounts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedAccounts.map(acc => (
              <span
                key={acc}
                className="flex items-center gap-1 bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm"
              >
                {acc}
                <button
                  onClick={() => removeAccount(acc)}
                  className="ml-1 text-blue-500 hover:text-blue-900"
                  title={`Remover ${acc}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="w-80">
          <AccountAutocomplete
            value={accountInput}
            accounts={availableAccounts}
            onChange={handleAccountInputChange}
            placeholder="Adicionar conta ao extrato..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Search className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
        </div>
        <div className={`grid grid-cols-1 gap-3 ${mode === 'list' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <input
            type="date"
            value={filterBegin}
            onChange={(e) => updateFilter('begin', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={filterEnd}
            onChange={(e) => updateFilter('end', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={filterDescription}
            onChange={(e) => updateFilter('description', e.target.value)}
            placeholder="Descrição"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          {mode === 'list' && (
            <input
              type="text"
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
              placeholder="Conta"
              className="border rounded-lg px-3 py-2 text-sm"
            />
          )}
        </div>
      </div>

      {/* REGISTER MODE */}
      {mode === 'register' && (
        <>
          {regError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {regError}
            </div>
          )}
          {regLoading ? (
            <div className="text-center py-12 text-gray-500">Carregando...</div>
          ) : regEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Nenhuma transação encontrada</div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
              <div className="px-4 py-2 border-b text-sm text-gray-500">
                {regEntries.length} de {regTotal}
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Descrição</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Contrapartidas</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Valor</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Saldo</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Docs</th>
                    {canWrite && (
                      <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {regEntries.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{entry.date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{entry.payee}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.postings && entry.postings.map((p, pi) => (
                          <div key={pi} className="flex justify-between gap-4">
                            <span className="truncate">{p.account}</span>
                            <span className={`tabular-nums whitespace-nowrap ${p.amount < 0 ? 'text-red-600' : 'text-green-700'}`}>
                              {fmtAmount(p.amount, p.commodity || 'BRL')}
                            </span>
                          </div>
                        ))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div>
                          <div className={`font-medium whitespace-nowrap ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmtAmount(entry.amount, entry.commodity || 'BRL')}
                          </div>
                          {entry.accounts && entry.accounts.length > 0 && entry.accounts.some(a => !selectedAccounts.includes(a.account)) && (
                            <div className="mt-1 space-y-0.5">
                              {entry.accounts.map((a, ai) => (
                                <div key={ai} className="text-xs text-gray-400 whitespace-nowrap">
                                  {a.account}{entry.accounts!.length > 1 ? `: ${fmtAmount(a.amount, a.commodity || 'BRL')}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right whitespace-nowrap text-gray-600">
                        {entry.running_balance && Object.keys(entry.running_balance).length > 0
                          ? Object.entries(entry.running_balance).map(([commodity, value], bi) => (
                              <div key={bi}>{fmtAmount(value as number, commodity || 'BRL')}</div>
                            ))
                          : fmtAmount(0, entry.commodity || 'BRL')
                        }
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.docs && entry.docs.length > 0 && (
                          <div className="flex items-center justify-center gap-1">
                            {entry.docs.map((docKey, di) => (
                              <a
                                key={di}
                                href={`/documentos?highlight=${encodeURIComponent(docKey)}`}
                                title={docKey}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                <FileText className="h-4 w-4" />
                              </a>
                            ))}
                          </div>
                        )}
                      </td>
                      {canWrite && (
                        <td className="px-4 py-3 text-center">
                          {entry.txn_id && (
                            <button
                              onClick={() => handleRegisterEditClick(entry)}
                              disabled={regEditLoading === entry.txn_id}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded disabled:opacity-50"
                              title="Editar"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} className="h-4" />
              {regLoadingMore && (
                <div className="text-center py-4 text-gray-500 text-sm">Carregando mais...</div>
              )}
            </div>
          )}
        </>
      )}

      {/* LIST MODE */}
      {mode === 'list' && (
        <>
          {initialLoading ? <LoadingSpinner /> :
          loading ? <div className="text-center py-12 text-gray-500">Carregando...</div> :
          <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {canWrite && <th className="px-3 py-3 w-8"></th>}
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Descrição</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Postings</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tags</th>
                    {canWrite && (
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={canWrite ? 6 : 4} className="text-center py-8 text-gray-500 text-sm">
                        Nenhum lançamento encontrado.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((xact) => (
                      <tr
                        key={xact.id}
                        className={`hover:bg-blue-50 ${selected.has(xact.id) ? 'bg-blue-100' : 'even:bg-gray-100'}`}
                      >
                        {canWrite && (
                          <td className="px-3 py-3 align-top">
                            <input
                              type="checkbox"
                              checked={selected.has(xact.id)}
                              onChange={() => toggleSelect(xact.id)}
                              className="rounded mt-0.5"
                              title="Selecionar para vincular como transferência"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {xact.date}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-1.5">
                            {xact.payee}
                            {(xact.doc_count ?? 0) > 0 && (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => handleDocClick(xact.id)}
                                  className="text-blue-500 hover:text-blue-700"
                                  title={`${xact.doc_count} documento(s)`}
                                >
                                  <Paperclip className="h-3.5 w-3.5" />
                                </button>
                                {docPopover === xact.id && (
                                  <div className="absolute z-10 left-0 top-6 bg-white border rounded-lg shadow-lg p-2 min-w-48">
                                    <div className="text-xs text-gray-500 mb-1">{docPopoverDocs.length} documento(s)</div>
                                    {docPopoverDocs.map((doc) => (
                                      <button
                                        key={doc.key}
                                        onClick={() => handleDocDownload(doc.key)}
                                        className="block w-full text-left text-xs text-blue-600 hover:bg-blue-50 rounded px-2 py-1 truncate"
                                      >
                                        {doc.key.split('/').pop()}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {xact.postings.map((p, i) => (
                            <div key={i} className="flex justify-between gap-4">
                              <span className="truncate">{p.account}</span>
                              <span className={`tabular-nums whitespace-nowrap ${(p.amount ?? 0) < 0 ? 'text-red-600' : 'text-green-700'}`}>
                                {formatBRL(p.amount ?? 0)}
                              </span>
                            </div>
                          ))}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {Object.entries(xact.tags || {}).map(([k, v]) => (
                            <span
                              key={k}
                              className="inline-block bg-gray-100 rounded-full px-2 py-0.5 text-xs mr-1 mb-1"
                            >
                              {k}: {v}
                            </span>
                          ))}
                        </td>
                        {canWrite && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => openEdit(xact)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                              title="Editar"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(xact.id)}
                                disabled={deleting === xact.id}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded ml-1"
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">
              {transactions.length} lançamento{transactions.length !== 1 ? 's' : ''}
            </div>
          </div>}

          {canWrite && selected.size > 0 && (
            <div className="sticky bottom-0 z-20 bg-white border rounded-lg shadow-lg p-3 flex items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{selected.size}</span> selecionado(s)
                {selected.size !== 2 && (
                  <span className="text-gray-400 ml-2">— selecione exatamente 2 para vincular</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Limpar
                </button>
                <button
                  onClick={handleLinkTransfer}
                  disabled={selected.size !== 2 || linking}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeftRight className="h-4 w-4" />
                  {linking ? 'Vinculando...' : 'Vincular como transferência'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
